import React, { useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { updateUserPin } from "../../../../../stores/slices/current-user-slice";
import { useCreatePinMutation } from "../../../../../service/customer-service-v2";
import { ButtonWithLoadingState } from "../../../../../common/components/button-with-loading-state/ButtonWithLoadingState";
import { getErrorMessage } from "../../../../../utils/error-handler";
import { describeError } from "../../../../../services/business-analytics/customFields";
import { trackSetTransactionPinFailed, trackSetTransactionPinSuccess } from "../../../../../services/business-analytics/transactionPinEvents";
import { CodePinFields, CodePinFieldsHandle } from "../../../../../common/components/code-pin-fields/CodePinFields";
import styles from "./setPinContent.module.scss";

export interface SetPinContentProps {
	onSetPinSubmitted: () => void;
}

export const SetPinContent: React.FC<SetPinContentProps> = ({ onSetPinSubmitted }) => {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const [pin, setPin] = useState("");
	const [confirmPin, setConfirmPin] = useState("");

	const confirmRef = useRef<CodePinFieldsHandle>(null);

	const [createPin, { isLoading }] = useCreatePinMutation();

	const isSubmitEnabled = pin.length === 4 && pin === confirmPin;

	const handleSubmit = async () => {
		try {
			await createPin({ pin }).unwrap();
			dispatch(updateUserPin({ hasPin: true }));
			trackSetTransactionPinSuccess();
			onSetPinSubmitted();
		} catch (err: any) {
			trackSetTransactionPinFailed(describeError(err));
			toast.error(getErrorMessage(err?.data) || t("account.web.failedToSetPin"));
		}
	};

	return (
		<div className={styles.content}>
			<p className={styles.label}>{t("pin.transactionPin")}</p>
			<CodePinFields numberOfFields={4} value={pin} onChange={setPin} obscureText autoFocus onComplete={() => confirmRef.current?.focus()} />

			<p className={`${styles.label} ${styles.labelSpaced}`}>{t("account.web.confirmTransactionPin")}</p>
			<CodePinFields ref={confirmRef} numberOfFields={4} value={confirmPin} onChange={setConfirmPin} obscureText />

			<div className={styles.btnWrapper}>
				<ButtonWithLoadingState buttonText={t("common.actions.continueKey")} loading={isLoading} buttonPressed={isSubmitEnabled ? handleSubmit : null} />
			</div>
		</div>
	);
};
